import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
} from '@mui/material';
import { Box } from '@mui/system';

import { useMount, useUpdateEffect } from 'react-use';
import moment from 'moment';
import CalendarGroups from '../../components/calendar/CalendarGroups';
import CalendarStatusCardByGroup from '../../components/calendar/CalendarStatusCardByGroup';
import NewCalendar from '../../components/calendar/NewCalendar';
import {
  closeModal,
  deleteItem,
  getData,
  updateItem,
  openUpdateModal,
  toggleGroup,
} from '../../modules/calendarBot';
import CalendarItemModal from '../../components/calendar/CalendarItemModal';
import {
  MODAL_TYPE_CALENDAR_DELETE,
  MODAL_TYPE_CALENDAR_UPDATE,
} from '../../common/consts';
import Toast from '../../components/Toast';
import { getManagerMode } from '../../common/util';

const CalendarStatusContainer = () => {
  const dispatch = useDispatch();
  const {
    selectDate,
    pending,
    robotData,
    calendarData,
    modal,
    register: { result: registerResult },
    update: {
      result: updateResult,
      message: updateMessage,
      error: updateError,
    },
    delete: {
      result: deleteResult,
      message: deleteMessage,
      error: deleteError,
    },
  } = useSelector((state) => state.calendarBot);
  const bManagerMode = getManagerMode();
  const [checkedData, setCheckedData] = useState({});
  const [snack, setSnack] = useState({ open: false });

  const handleSelectDate = (v) => {
    dispatch(getData(v));
  };

  const handleToggleGroup = (id) => (e) => {
    const { checked } = e.target;
    dispatch(toggleGroup({ id, checked }));
  };

  const handleChangeCheck = (e) => {
    const { checked, value } = e.target;
    const { _id: itemId, ...data } = JSON.parse(value);
    if (checked) {
      checkedData[itemId] = { ...data, _id: itemId };
    } else {
      delete checkedData[itemId];
    }
    setCheckedData({ ...checkedData });
  };

  const handleUpdateClick = () => {
    dispatch(
      openUpdateModal({
        type: MODAL_TYPE_CALENDAR_UPDATE,
        list: Object.values(checkedData),
        onUpdate: (list) => dispatch(updateItem({ list })),
      }),
    );
  };
  const handleDeleteClick = () => {
    dispatch(
      openUpdateModal({
        type: MODAL_TYPE_CALENDAR_DELETE,
        list: Object.values(checkedData),
        onUpdate: (list) => dispatch(deleteItem({ list })),
      }),
    );
  };

  const handleModalUpdate = (list) => {
    if (modal.type === MODAL_TYPE_CALENDAR_UPDATE) {
      dispatch(updateItem({ list }));
    }
    if (modal.type === MODAL_TYPE_CALENDAR_DELETE) {
      dispatch(deleteItem({ list }));
    }
  };

  const handleModalClose = () => {
    dispatch(closeModal());
    setCheckedData({});
  };

  const handleSnackClose = () => {
    setSnack({ open: false });
  };

  useUpdateEffect(() => {
    if (updateResult !== null) {
      if (updateResult) {
        dispatch(getData(moment()));
      }
      handleModalClose();
      setSnack({
        open: true,
        onClose: handleSnackClose,
        message: updateResult ? updateMessage : updateError,
        severity: updateResult ? 'success' : 'error',
      });
    }
  }, [updateResult]);

  useUpdateEffect(() => {
    if (deleteResult !== null) {
      if (deleteResult) {
        dispatch(getData(moment()));
      }
      handleModalClose();
      setSnack({
        open: true,
        onClose: handleSnackClose,
        message: deleteResult ? deleteMessage : deleteError,
        severity: deleteResult ? 'success' : 'error',
      });
    }
  }, [deleteResult]);

  useUpdateEffect(
    () => (registerResult ? dispatch(getData(moment())) : null),
    [registerResult],
  );

  useMount(() => {
    dispatch(getData(moment()));
  });

  return (
    <>
      <Card sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            m: 2,
          }}
        >
          <Typography variant="h5">일정 관리</Typography>
        </Box>
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} sx={{ overflowX: 'auto' }}>
              {calendarData && calendarData.length > 0 && (
                <NewCalendar
                  {...{
                    selectDate,
                    robotData,
                    calendarData,
                    handleSelectDate,
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <CalendarGroups
                robotData={robotData}
                onToggleGroup={handleToggleGroup}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">
                {`${selectDate.format('LL')} 일정`}
              </Typography>
              <Grid container item xs={12} spacing={1}>
                {pending && (
                  <LinearProgress
                    color="secondary"
                    sx={{ width: '100%', my: 2 }}
                  />
                )}
                {!pending &&
                  calendarData &&
                  calendarData.length >= selectDate.get('date') &&
                  calendarData[selectDate.get('date') - 1].length === 0 && (
                    <Grid item xs={12} sx={{ my: 2, mx: 1 }}>
                      등록된 일정이 없습니다.
                    </Grid>
                  )}
                {!pending &&
                  calendarData &&
                  calendarData.length >= selectDate.get('date') &&
                  calendarData[selectDate.get('date') - 1].length > 0 &&
                  calendarData[selectDate.get('date') - 1].map(
                    ({ id, name, list, className }, idx) => {
                      return (
                        robotData[id].checked &&
                        idx % 4 < 4 &&
                        list.length > 0 && (
                          <Grid
                            item
                            lg={4}
                            md={6}
                            xs={12}
                            key={`${selectDate.format('YYYY-MM-DD')}_${name}`}
                            sx={{ mt: 2 }}
                          >
                            <CalendarStatusCardByGroup
                              {...{
                                className,
                                name,
                                date: selectDate.get('date'),
                                id,
                                list,
                                checkedData,
                                handleUpdateClick,
                                handleDeleteClick,
                                handleChangeCheck,
                                bManagerMode,
                              }}
                            />
                          </Grid>
                        )
                      );
                    },
                  )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {modal.open && (
        <CalendarItemModal
          {...{
            ...modal,
            onUpdate: handleModalUpdate,
            onCancel: handleModalClose,
          }}
        />
      )}
      <Toast {...{ ...snack }} />
    </>
  );
};

export default CalendarStatusContainer;
